/**
 * Validierungs-Utility für den Buchungsbereich
 * Implementiert Zeitvalidierungen und Verfügbarkeitsprüfungen
 */

// Konstanten für Validierung und Zeitbeschränkungen
const VALIDATION_CONSTANTS = {
  MIN_BOOKING_HOURS: 1,
  // Neue Zeitlogik: Wochentag = Mo-Do, Wochenende = Fr-So
  WEEKDAY_START_HOUR: 8,    // Früheste Startzeit unter der Woche
  WEEKDAY_END_HOUR: 22,     // Späteste Endzeit unter der Woche
  WEEKEND_START_HOUR: 8,    // Früheste Startzeit am Wochenende
  // Wochenende: Keine Einschränkung für Endzeit
  MIN_ADVANCE_DAYS: 3,      // Mindestens 3 Tage im Voraus buchen
  MAX_ADVANCE_YEARS: 1,     // Maximal 1 Jahr im Voraus anfragen (Enddatum)
};

/**
 * Maximales Enddatum für Anfragen (heute + MAX_ADVANCE_YEARS)
 * @returns {Date} Maximales buchbares Datum (Mitternacht)
 */
export function getMaxBookableDate() {
  const maxDate = new Date();
  maxDate.setHours(0, 0, 0, 0);
  maxDate.setFullYear(maxDate.getFullYear() + VALIDATION_CONSTANTS.MAX_ADVANCE_YEARS);
  return maxDate;
}

/**
 * Prüft ob ein Datum am Wochenende liegt
 * Neue Logik: Freitag-Sonntag ganztägig = Wochenende, Montag-Donnerstag = Wochentag
 * @param {Date} date - Zu prüfendes Datum
 * @param {string} time - Zeit im Format "HH:MM" (wird nicht mehr verwendet, für Kompatibilität beibehalten)
 * @returns {boolean} true wenn es sich um einen Wochenendzeitpunkt handelt
 */
export function isWeekendTime(date, time) {
  const day = date.getDay();
  
  // Neue Logik: Freitag (5), Samstag (6), Sonntag (0) = Wochenende
  // Montag (1), Dienstag (2), Mittwoch (3), Donnerstag (4) = Wochentag
  return day === 0 || day === 5 || day === 6;
}

/**
 * Prüft ob ein Datum für Buchungen verfügbar ist
 * - Mindestens 3 Tage Vorlauf (bei Wochenende: Freitag der Periode)
 * - Maximal 1 Jahr im Voraus (Enddatum)
 * @param {Date} startDate - Startdatum der Buchung
 * @param {Date} endDate - Enddatum der Buchung
 * @returns {Object} Verfügbarkeitsstatus mit isBookable, reason und code
 */
export function isDateBookable(startDate, endDate) {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Setze auf Mitternacht für genauen Vergleich
  
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  // Maximal 1 Jahr im Voraus (bezogen auf Enddatum)
  const maxBookableDate = getMaxBookableDate();
  if (end > maxBookableDate) {
    return {
      isBookable: false,
      reason: `Anfragen sind maximal ${VALIDATION_CONSTANTS.MAX_ADVANCE_YEARS} Jahr im Voraus möglich`,
      code: 'TOO_FAR'
    };
  }
  
  // Prüfe ob die Buchung Wochenendtage enthält
  const hasWeekendDays = hasWeekendInPeriod(startDate, endDate, "00:00", "23:59");
  
  if (hasWeekendDays) {
    // Für Wochenend-Buchungen: Finde den Freitag der Woche
    const fridayOfWeek = findFridayOfBookingPeriod(startDate, endDate);
    
    if (fridayOfWeek) {
      const daysDifference = Math.floor((fridayOfWeek.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDifference < VALIDATION_CONSTANTS.MIN_ADVANCE_DAYS) {
        return {
          isBookable: false,
          reason: `Wochenend-Buchungen müssen mindestens ${VALIDATION_CONSTANTS.MIN_ADVANCE_DAYS} Tage im Voraus erfolgen (Freitag muss ${VALIDATION_CONSTANTS.MIN_ADVANCE_DAYS} Tage entfernt sein)`,
          code: 'TOO_SOON'
        };
      }
    }
  } else {
    // Für reine Wochentag-Buchungen: Startdatum muss 3 Tage entfernt sein
    const daysDifference = Math.floor((start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDifference < VALIDATION_CONSTANTS.MIN_ADVANCE_DAYS) {
      return {
        isBookable: false,
        reason: `Buchungen müssen mindestens ${VALIDATION_CONSTANTS.MIN_ADVANCE_DAYS} Tage im Voraus erfolgen`,
        code: 'TOO_SOON'
      };
    }
  }
  
  return {
    isBookable: true,
    reason: null,
    code: null
  };
}

/**
 * Findet den Freitag in einem Buchungszeitraum
 * @param {Date} startDate - Startdatum
 * @param {Date} endDate - Enddatum
 * @returns {Date|null} Freitag-Datum oder null wenn kein Freitag im Zeitraum
 */
function findFridayOfBookingPeriod(startDate, endDate) {
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  
  while (current <= end) {
    if (current.getDay() === 5) { // Freitag
      return new Date(current);
    }
    current.setDate(current.getDate() + 1);
  }
  
  return null;
}

/**
 * Rundet eine Zeit auf die nächste halbe Stunde
 * @param {string} time - Zeit im Format "HH:MM"
 * @param {boolean} roundUp - true für aufrunden, false für abrunden
 * @returns {string} Gerundete Zeit im Format "HH:MM"
 */
export function roundTimeToHalfHour(time, roundUp = true) {
  const [hour, minute] = time.split(':').map(Number);
  
  if (minute === 0 || minute === 30) return time;
  
  if (roundUp) {
    if (minute > 30) {
      return `${(hour + 1).toString().padStart(2, '0')}:00`;
    } else {
      return `${hour.toString().padStart(2, '0')}:30`;
    }
  } else {
    if (minute > 30) {
      return `${hour.toString().padStart(2, '0')}:30`;
    } else {
      return `${hour.toString().padStart(2, '0')}:00`;
    }
  }
}

/**
 * Prüft ob ein Wochenende im Zeitraum liegt
 * Neue Logik: Freitag-Sonntag ganztägig = Wochenende
 */
function hasWeekendInPeriod(startDate, endDate, startTime, endTime) {
  // Erstelle Start- und End-DateTime
  const start = new Date(startDate);
  const [startHour, startMinute] = startTime.split(':').map(Number);
  start.setHours(startHour, startMinute, 0, 0);
  
  const end = new Date(endDate);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  end.setHours(endHour, endMinute, 0, 0);
  
  // Iteriere über jede Minute
  const current = new Date(start);
  while (current < end) {
    const day = current.getDay();
    
    // Neue Logik: Prüfe ob es Wochenende ist (Fr-So)
    if (day === 0 || day === 5 || day === 6) return true;
    
    // Nächste Minute
    current.setMinutes(current.getMinutes() + 1);
  }
  
  return false;
}

/**
 * Validiert einen Buchungszeitraum mit neuen Zeitbeschränkungen
 * @param {Object} params - Validierungsparameter
 * @param {string} params.startTime - Startzeit
 * @param {Date} params.startDate - Startdatum
 * @param {string} params.endTime - Endzeit
 * @param {Date} params.endDate - Enddatum
 * @returns {Object} Validierungsergebnis
 */
export function validateBookingTime({ startTime, endTime, startDate, endDate }) {
  const errors = [];
  
  // Mindestzeit (1 Stunde)
  const startHour = parseInt(startTime.split(':')[0]);
  const startMinute = parseInt(startTime.split(':')[1]);
  const endHour = parseInt(endTime.split(':')[0]);
  const endMinute = parseInt(endTime.split(':')[1]);
  
  let totalMinutes = 0;
  
  if (startDate.getTime() === endDate.getTime()) {
    // Gleicher Tag
    totalMinutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
  } else {
    // Über Mitternacht
    totalMinutes = (24 * 60 - (startHour * 60 + startMinute)) + (endHour * 60 + endMinute);
  }
  
  if (totalMinutes < VALIDATION_CONSTANTS.MIN_BOOKING_HOURS * 60) {
    errors.push(`Mindestbuchungszeit beträgt ${VALIDATION_CONSTANTS.MIN_BOOKING_HOURS} Stunde`);
  }
  
  // Startzeit muss vor Endzeit liegen (bei gleichem Tag)
  if (startDate.getTime() === endDate.getTime() && startTime >= endTime) {
    errors.push("Endzeit muss nach Startzeit liegen");
  }
  
  // Neue Zeitbeschränkungen basierend auf Wochentag/Wochenende
  const startDay = startDate.getDay();
  const endDay = endDate.getDay();
  
  // Startzeit-Beschränkungen (gilt für alle Tage)
  if (startHour < VALIDATION_CONSTANTS.WEEKDAY_START_HOUR) {
    errors.push(`Früheste Startzeit ist ${VALIDATION_CONSTANTS.WEEKDAY_START_HOUR.toString().padStart(2, '0')}:00 Uhr`);
  }
  
  // Endzeit-Beschränkungen nur unter der Woche (Mo-Do)
  const isStartWeekday = startDay >= 1 && startDay <= 4;
  const isEndWeekday = endDay >= 1 && endDay <= 4;
  const isEndWeekend = endDay === 0 || endDay === 5 || endDay === 6;
  
  // Endzeit-Validierung nur für Wochentage
  if (isEndWeekday) {
    if (endHour > VALIDATION_CONSTANTS.WEEKDAY_END_HOUR) {
      errors.push(`Späteste Endzeit unter der Woche ist ${VALIDATION_CONSTANTS.WEEKDAY_END_HOUR.toString().padStart(2, '0')}:00 Uhr`);
    }
  }
  // Am Wochenende: Keine Endzeit-Beschränkung (0:00-23:30 erlaubt)
  
  // Späteste Startzeit (gilt für alle Tage)
  if (startHour > VALIDATION_CONSTANTS.WEEKDAY_END_HOUR) {
    errors.push(`Späteste Startzeit ist ${VALIDATION_CONSTANTS.WEEKDAY_END_HOUR.toString().padStart(2, '0')}:00 Uhr`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    totalMinutes
  };
}
